import { DateTime } from 'luxon';
import { CommissionYearStatistics } from 'src/adminPanelV2/api/commissionsV2';

interface CommissionStatsMonth {
    month: number;
    localizedMonth: string;
    totalSelectedYear: number;
    totalComparisonYear: number;
    comparisonPercentage: number;
    overviewResourceUrl?: string;
    detailResourceUrl?: string;
    payoutDate?: DateTime;
}

export default class CommissionStats {
    public readonly totalSelectedYear: number;
    public readonly totalComparisonYear: number;
    public readonly comparisonPercentage?: number;
    public readonly total: number;
    public readonly months: CommissionStatsMonth[];
    public readonly currencyIso: string;

    constructor(json: any) {
        this.totalSelectedYear = json.totalSelectedYear;
        this.totalComparisonYear = json.totalComparisonYear;
        this.comparisonPercentage = json.comparisonPercentage != null ? json.comparisonPercentage : undefined;
        this.total = json.total;
        this.months = json.months.map(m => ({
            month: m.month,
            localizedMonth: m.localizedMonth,
            totalSelectedYear: m.totalSelectedYear == null ? 0 : m.totalSelectedYear,
            totalComparisonYear: m.totalComparisonYear == null ? 0 : m.totalComparisonYear,
            comparisonPercentage: m.comparisonPercentage == null ? 0 : m.comparisonPercentage,
            overviewResourceUrl: m.overviewResourceUrl == null ? undefined : m.overviewResourceUrl,
            detailResourceUrl: m.detailResourceUrl == null ? undefined : m.detailResourceUrl,
            payoutDate: m.payoutDate == null ? undefined : DateTime.fromISO(m.payoutDate),
        }));
        this.currencyIso = json.currencyIso || 'EUR';
    }
}

/** The served Vorjahresvergleich rule (round((current − base) / base × 100), none on a 0 base) for another compare year. */
const deltaPercent = (current: number, base: number): number | null =>
    base === 0 ? null : Math.round(((current - base) / base) * 100);

/**
 * R65 item 8c: Mein Bereich's statistics from ordering's live year read (`/commissions/me/statistics`)
 * in the shape the views render — the compare year's sums come from its own read, the served
 * percents are kept when it is the previous year.
 */
export function commissionStatsFromYearStatistics(
    selected: CommissionYearStatistics,
    compare: CommissionYearStatistics,
    locale: string
) {
    const compareSums = new Map(compare.monthlySums.map(entry => [entry.month, entry.sum]));
    const previousYear = compare.year === selected.year - 1;
    return {
        totalSelectedYear: selected.yearTotal,
        totalComparisonYear: compare.yearTotal,
        comparisonPercentage: previousYear
            ? selected.previousYearDeltaPercent
            : deltaPercent(selected.yearTotal, compare.yearTotal),
        total: selected.allTimeTotal,
        months: selected.monthlySums.map(entry => {
            const compareSum = compareSums.get(entry.month) ?? 0;
            return {
                month: entry.month,
                localizedMonth: DateTime.fromObject({ year: selected.year, month: entry.month })
                    .setLocale(locale)
                    .toFormat('LLLL'),
                totalSelectedYear: entry.sum,
                totalComparisonYear: compareSum,
                comparisonPercentage: previousYear
                    ? entry.previousYearDeltaPercent
                    : deltaPercent(entry.sum, compareSum),
            };
        }),
    };
}
